// Helpers for the People section (Customers, Teams, Agents, Groups)

import { GROUPS_DUMMY } from "../constants/peopleData";

// Status chip styles (outlined pill look similar to screenshots)
export const getCustomerStatusStyle = (status) => {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 24,
    width: status === "inactive" || status === "archived" ? 72 : 64,
    borderRadius: 100,
    border: "1px solid",
    padding: "0 10px",
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
    flexShrink: 0,
    boxSizing: "border-box",
  };
  const s = (status || "").toLowerCase();
  if (s === "active") {
    return { ...base, color: "#405261", borderColor: "#52C41A", background: "#F6FFED" };
  }
  if (s === "waitingreply" || s === "waiting reply") {
    return { ...base, color: "#405261", borderColor: "#FA8C16", background: "#FFFBE6" };
  }
  if (s === "inactive") {
    return { ...base, color: "#405261", borderColor: "#C5CED6", background: "#FAFAFA" };
  }
  if (s === "blocked") {
    return { ...base, color: "#405261", borderColor: "#FA8C16", background: "#FFF7E6" };
  }
  if (s === "archived") {
    return { ...base, color: "#405261", borderColor: "#D9D9D9", background: "#FAFAFA" };
  }
  return { ...base, color: "#405261", borderColor: "#D5DCE1", background: "#FFF" };
};

export const getCustomerStatusLabel = (status) => {
  const map = {
    active: "Active",
    waitingReply: "Waiting Reply",
    inactive: "Inactive",
    blocked: "Banned",
    archived: "Archived",
    online: "Online",
    offline: "Offline",
    suspended: "Suspended",
  };
  return map[status] || status;
};

// Agent status dot color map
export const getAgentStatusDot = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "online") return "#52C41A";
  if (s === "inactive") return "#FF4D4F";
  if (s === "offline") return "#93A1B0";
  if (s === "suspended") return "#FAAD14";
  return "#93A1B0";
};

// Generic counter for a given dataset, grouped by status field.
export const buildStatusCounts = (items = [], statusField = "status") => {
  return items.reduce(
    (acc, item) => {
      const key = (item[statusField] || "").toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      acc.all = (acc.all || 0) + 1;
      return acc;
    },
    { all: 0 }
  );
};

// Filter helpers
export const filterCustomers = (rows, { tab, search, groupFilters }) => {
  return (rows || []).filter((r) => {
    if (tab && tab !== "all" && r.status !== tab) return false;
    if (groupFilters?.length) {
      const ids = groupFilters.filter(Boolean);
      const rowGroupIds = r.groupIds || [];
      if (ids.length && !ids.some((id) => rowGroupIds.includes(id))) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (
        !(r.name || "").toLowerCase().includes(q) &&
        !(r.email || "").toLowerCase().includes(q) &&
        !(r.phone || "").toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    return true;
  });
};

export const filterTeams = (rows, { tab, search, groupFilters }) => {
  return (rows || []).filter((r) => {
    if (tab && tab !== "all" && r.status !== tab) return false;
    if (groupFilters?.length) {
      const ids = groupFilters.filter(Boolean);
      const teamGroupIds = r.groupIds || [];
      if (ids.length && !ids.some((id) => teamGroupIds.includes(id))) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (
        !(r.name || "").toLowerCase().includes(q) &&
        !(r.description || "").toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    return true;
  });
};

export const filterGroups = (rows, { tab, search, status }) => {
  return (rows || []).filter((r) => {
    if (tab && tab !== "all" && r.status !== tab) return false;
    if (status && r.status !== status) return false;
    if (search && !(r.name || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
};

export const filterAgents = (rows, { presence }) => {
  return (rows || []).filter((r) => {
    if (presence && r.presence !== presence) return false;
    return true;
  });
};

// Provide dataset loaders so pages share a single source of truth.
export const getGroupsSeed = () => [...GROUPS_DUMMY];
export const getAgentsSeed = () => [...AGENTS_DUMMY];

/** Customer edit page e.g. /people/customers/:id/edit */
export const isCustomerEditRoute = (pathname) =>
  Boolean(pathname && /\/people\/customers\/[^/]+\/edit$/.test(pathname));

/** Customer detail view e.g. /people/customers/:id */
export const isCustomerDetailRoute = (pathname) =>
  Boolean(pathname && /\/people\/customers\/[^/]+$/.test(pathname)) &&
  !isCustomerEditRoute(pathname);

export const isAgentEditRoute = (pathname) =>
  Boolean(pathname && /\/people\/agents\/[^/]+\/edit$/.test(pathname));

export const isAgentDetailRoute = (pathname) =>
  Boolean(pathname && /\/people\/agents\/[^/]+$/.test(pathname)) && !isAgentEditRoute(pathname);

/** Group detail view e.g. /people/groups/:id */
export const isGroupDetailRoute = (pathname) =>
  Boolean(pathname && /\/people\/groups\/[^/]+$/.test(pathname));

/** Team edit page e.g. /people/teams/:id/edit */
export const isTeamEditRoute = (pathname) =>
  Boolean(pathname && /\/people\/teams\/[^/]+\/edit$/.test(pathname));

/** Team detail view e.g. /people/teams/:id */
export const isTeamDetailRoute = (pathname) =>
  Boolean(pathname && /\/people\/teams\/[^/]+$/.test(pathname)) && !isTeamEditRoute(pathname);

/** Route detection for People dummy pages */
export const getPeoplePageKey = (pathname) => {
  if (!pathname) return null;
  if (
    isCustomerDetailRoute(pathname) ||
    isCustomerEditRoute(pathname) ||
    isTeamDetailRoute(pathname) ||
    isTeamEditRoute(pathname) ||
    isAgentDetailRoute(pathname) ||
    isAgentEditRoute(pathname) ||
    isGroupDetailRoute(pathname)
  ) {
    return null;
  }
  if (pathname.includes("/people/customers")) return "customers";
  if (pathname.includes("/people/teams")) return "teams";
  if (pathname.includes("/people/agents")) return "agents";
  if (pathname.includes("/people/groups")) return "groups";
  return null;
};

export const isPeopleDashboardRoute = (pathname) => Boolean(getPeoplePageKey(pathname));

export const getPeopleInlineHeaderMeta = (pathname) => {
  const key = getPeoplePageKey(pathname);
  if (!key) return null;
  switch (key) {
    case "customers":
      return { title: "Customers", primaryLabel: "Add Customer" };
    case "teams":
      return { title: "Teams", primaryLabel: "Create Team" };
    case "agents":
      return { title: "Agents", primaryLabel: "Add Agent" };
    case "groups":
      return { title: "Groups", primaryLabel: "Create Group" };
    default:
      return null;
  }
};

/** Tab counts from seed data (header stays aligned with dummy dataset) */
export const getGroupSeedTabCounts = () => buildStatusCounts(GROUPS_DUMMY, "status");

export const getAgentSeedTabCounts = () => ({
  all: 0,
  online: 0,
  offline: 0,
  suspended: 0,
});

/**
 * Merge URLSearchParams for People filters (replace mode).
 * @param {import('react-router-dom').SetURLSearchParams} setSearchParams
 * @param {Record<string, string | undefined | null>} patch
 */
export const mergePeopleSearchParams = (setSearchParams, patch) => {
  setSearchParams(
    (prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    },
    { replace: true }
  );
};

export const MOCK_PEOPLE_TABLE_LOAD_MS = 750;

export function mockPeopleTableLoad(ms = MOCK_PEOPLE_TABLE_LOAD_MS) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
